import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { HighlightedText } from '../common/highlightText';
import { filterGlossary, MARKING_METRICS_GLOSSARY } from './markingMetricsGlossary';

interface MarkingMetricsGlossaryDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function MarkingMetricsGlossaryDialog({
  open,
  onClose,
}: MarkingMetricsGlossaryDialogProps) {
  const [query, setQuery] = useState('');
  const matches = filterGlossary(MARKING_METRICS_GLOSSARY, query);
  const searching = query.trim().length > 0;

  const handleClose = () => {
    onClose();
    setQuery('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="marking-metrics-glossary-title"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            id="marking-metrics-glossary-title"
            variant="h6"
            component="span"
            sx={{ flexGrow: 1 }}
          >
            Glossary
          </Typography>
          <IconButton aria-label="Close glossary" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            pb: 1.5,
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search terms…"
            inputProps={{ 'aria-label': 'Search glossary' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {searching
              ? `${matches.length} of ${MARKING_METRICS_GLOSSARY.length} terms match`
              : `${MARKING_METRICS_GLOSSARY.length} terms`}
          </Typography>
        </Box>

        {matches.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No terms match “{query.trim()}”.
          </Typography>
        ) : (
          matches.map((entry, index) => (
            <Box key={entry.term}>
              {index > 0 && <Divider sx={{ my: 1.5 }} />}
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                <HighlightedText text={entry.term} query={query} />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <HighlightedText text={entry.definition} query={query} />
              </Typography>
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
}
